/**
 * ValidationEngine — orchestrates all validators to produce validation results.
 *
 * Combines LuhnValidator, BrandDetector, ExpiryValidator, and CVCValidator
 * to produce validation results with error codes and messages for change event payloads.
 *
 * Requirements: 10.6, 10.7
 */
import { LuhnValidator } from "./LuhnValidator";
import { BrandDetector, type CardBrand } from "./BrandDetector";
import { ExpiryValidator } from "./ExpiryValidator";
import { CVCValidator } from "./CVCValidator";

export interface FieldValidationResult {
  valid: boolean;
  complete: boolean;
  empty: boolean;
  brand: CardBrand | null;
  error: { code: string; message: string } | null;
}

export interface CardData {
  cardNumber?: string;
  expiry?: string;
  cvc?: string;
}

export const ValidationEngine = {
  /**
   * Validate a card number field.
   */
  validateCardNumber(cardNumber: string): FieldValidationResult {
    if (!cardNumber || cardNumber.length === 0) {
      return { valid: false, complete: false, empty: true, brand: null, error: null };
    }

    const brandInfo = BrandDetector.detect(cardNumber);
    const brand = brandInfo?.brand ?? null;

    // Still typing — not enough digits yet
    if (brandInfo && cardNumber.length < Math.min(...brandInfo.panLengths)) {
      return { valid: true, complete: false, empty: false, brand, error: null };
    }

    // No brand detected and still short
    if (!brandInfo && cardNumber.length < 13) {
      return { valid: true, complete: false, empty: false, brand: null, error: null };
    }

    // Check valid length for brand
    if (brandInfo && !BrandDetector.isValidLength(cardNumber, brandInfo)) {
      // If shorter than max, still typing
      if (cardNumber.length < Math.max(...brandInfo.panLengths)) {
        return { valid: true, complete: false, empty: false, brand, error: null };
      }
      return {
        valid: false, complete: false, empty: false, brand,
        error: { code: "invalid_card_number", message: "Card number has invalid length" },
      };
    }

    // Luhn check
    if (!LuhnValidator.isValid(cardNumber)) {
      return {
        valid: false, complete: false, empty: false, brand,
        error: { code: "invalid_card_number", message: "Card number is invalid" },
      };
    }

    // Valid and complete
    return { valid: true, complete: true, empty: false, brand, error: null };
  },

  /**
   * Validate an expiry field.
   */
  validateExpiry(expiry: string, now?: Date): FieldValidationResult {
    if (!expiry || expiry.length === 0) {
      return { valid: false, complete: false, empty: true, brand: null, error: null };
    }

    // Partial input — still typing
    if (!/^\d{2}\/\d{2}$/.test(expiry)) {
      return { valid: true, complete: false, empty: false, brand: null, error: null };
    }

    if (!ExpiryValidator.isValid(expiry, now)) {
      return {
        valid: false, complete: false, empty: false, brand: null,
        error: { code: "invalid_expiry", message: "Expiry date is invalid or in the past" },
      };
    }

    return { valid: true, complete: true, empty: false, brand: null, error: null };
  },

  /**
   * Validate a CVC field.
   */
  validateCVC(cvc: string, brand: CardBrand | null): FieldValidationResult {
    if (!cvc || cvc.length === 0) {
      return { valid: false, complete: false, empty: true, brand, error: null };
    }

    const expectedLength = brand === "AMEX" ? 4 : 3;

    // Still typing
    if (/^\d+$/.test(cvc) && cvc.length < expectedLength) {
      return { valid: true, complete: false, empty: false, brand, error: null };
    }

    if (!CVCValidator.isValid(cvc, brand)) {
      return {
        valid: false, complete: false, empty: false, brand,
        error: { code: "invalid_cvc", message: "CVC is invalid" },
      };
    }

    return { valid: true, complete: true, empty: false, brand, error: null };
  },

  /**
   * Validate all card fields and determine overall completeness.
   * Returns individual field results plus an overall `complete` flag.
   */
  validateAll(data: CardData, now?: Date): {
    cardNumber: FieldValidationResult;
    expiry: FieldValidationResult;
    cvc: FieldValidationResult;
    complete: boolean;
  } {
    const cardNumberResult = this.validateCardNumber(data.cardNumber ?? "");
    const expiryResult = this.validateExpiry(data.expiry ?? "", now);
    const cvcResult = this.validateCVC(data.cvc ?? "", cardNumberResult.brand);

    const complete =
      cardNumberResult.complete && expiryResult.complete && cvcResult.complete;

    return { cardNumber: cardNumberResult, expiry: expiryResult, cvc: cvcResult, complete };
  },
};
